import { BaseCoverageId, getBaseCoverage, primaryLineId } from "@/config/coverages";
import { OfferResponse } from "@/types/simpego-api";
import { getOfferCoverage } from "@/utils/coveragePayload";
import { formatSumInsured } from "@/utils/sumInsured";

export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** What a tier offers for one comparison row. */
export interface MetricValue {
  main: string;
  /** Smaller line below the figure. */
  sub?: string;
}

export interface CheckValue {
  isCovered: boolean;
  detail?: string;
  /** Covered rows whose detail text says it all: no checkmark in front of it. */
  hideCheckmark?: boolean;
}

/** The comparison is built in three layers: the few figures that really differ between the
 * packages (metric rows), the sub-coverages as a check matrix, and what is the same everywhere
 * (shown once below the table instead of as repeated sections). */
export type TableRow =
  | { kind: "section"; labelKey: string; infoKey?: string; infoTitleKey?: string; /** A subheading inside the section above (the Bausteine): no band, its rows are indented. */ sub?: boolean }
  | { kind: "metric"; labelKey: string; infoKey?: string; infoTitleKey?: string; nested?: boolean; value: (offer: OfferResponse | null, t: Translate) => MetricValue | null }
  | { kind: "check"; labelKey: string; infoKey?: string; infoTitleKey?: string; /** A Baustein: belongs to the main coverage above it, so it is shown indented. */ nested?: boolean; value: (offer: OfferResponse | null, t: Translate) => CheckValue };

const formatChf = (value: number): string => `CHF ${value.toLocaleString("de-CH")}`;

export const buildRows = (coverageSelections: Partial<Record<BaseCoverageId, boolean>>, isYoung: boolean): TableRow[] => {
  const rows: TableRow[] = [];
  // Krankheit and Unfall are one API coverage with shared figures, so they are compared once, on the
  // primary line, instead of as two sections that repeat each other.
  const primary = primaryLineId(coverageSelections);
  const diseaseOn = !!coverageSelections.disease;

  if (primary) {
    const def = getBaseCoverage(primary);
    rows.push({
      kind: "section",
      labelKey: coverageSelections.disease && coverageSelections.accident ? "coverages.diseaseAndAccident.label" : def.labelKey,
    });
    const band = (offer: OfferResponse | null, other: boolean): number | undefined => {
      const cov = getOfferCoverage(offer, def);
      const useUpTo9 = other ? !isYoung : isYoung;
      return useUpTo9 ? cov?.deductiblePercentageUpTo9Yrs : cov?.deductiblePercentageOver9Yrs;
    };

    rows.push({
      kind: "metric",
      labelKey: "coverages.sumInsuredLabel",
      infoKey: "coverages.sumInsuredInfoDescription",
      infoTitleKey: "coverages.sumInsuredInfoTitle",
      value: (offer, t) => {
        const sum = getOfferCoverage(offer, def)?.sumInsured;
        return typeof sum === "number" ? { main: formatSumInsured(sum, t) } : null;
      },
    });
    rows.push({
      kind: "metric",
      labelKey: "coverages.costShare.label",
      infoKey: "coverages.costShare.info",
      infoTitleKey: "coverages.costShare.infoTitle",
      value: (offer, t) => {
        const current = band(offer, false);
        if (typeof current !== "number") return null;
        const otherBand = band(offer, true);
        return {
          main: `${100 - current}%`,
          sub:
            typeof otherBand === "number"
              ? t(isYoung ? "priceCustomization.table.otherBandOver9" : "priceCustomization.table.otherBandUpTo9", { pct: 100 - otherBand })
              : undefined,
        };
      },
    });
    rows.push({
      kind: "metric",
      labelKey: "coverages.annualDeductibleLabel",
      infoKey: "coverages.annualDeductibleInfo",
      infoTitleKey: "coverages.annualDeductibleInfoTitle",
      value: (offer, t) => {
        const franchise = getOfferCoverage(offer, def)?.annualDeductible;
        if (typeof franchise !== "number") return null;
        return { main: franchise > 0 ? formatChf(franchise) : t("priceCustomization.table.none") };
      },
    });

    rows.push({ kind: "section", labelKey: "coverages.bausteine.label", infoKey: "coverages.bausteine.info", infoTitleKey: "coverages.bausteine.infoTitle", sub: true });
    for (const sub of def.subs.filter((s) => !s.diseaseOnly || diseaseOn)) {
      rows.push({
        kind: "check",
        nested: true,
        labelKey: sub.labelKey,
        infoKey: sub.infoKey,
        infoTitleKey: sub.infoTitleKey,
        value: (offer, t) => {
          const entry = getOfferCoverage(offer, def)?.[sub.id];
          const sum = entry?.sumInsured;
          const isCovered = sub.fixed ? true : !!entry?.requested;
          return {
            isCovered,
            detail: isCovered && typeof sum === "number" && sum > 0 ? formatSumInsured(sum, t) : undefined,
          };
        },
      });
    }

    // Volldeckung bei Erbkrankheiten: a plain flag that only exists together with Krankheit.
    if (diseaseOn) {
      rows.push({
        kind: "check",
        nested: true,
        labelKey: "coverages.subs.waiverHereditaryLimits.label",
        infoKey: "coverages.subs.waiverHereditaryLimits.info",
        infoTitleKey: "coverages.subs.waiverHereditaryLimits.infoTitle",
        value: (offer) => ({ isCovered: !!getOfferCoverage(offer, def)?.waiverHereditaryLimits?.requested }),
      });
    }
  }

  if (coverageSelections.preexistingConditions) {
    const def = getBaseCoverage("preexistingConditions");
    rows.push({ kind: "section", labelKey: def.labelKey, infoKey: def.infoDescriptionKey, infoTitleKey: def.infoTitleKey });
    rows.push({
      kind: "metric",
      labelKey: "coverages.sumInsuredLabel",
      infoKey: "coverages.sumInsuredInfoDescription",
      infoTitleKey: "coverages.sumInsuredInfoTitle",
      value: (offer, t) => {
        const sum = getOfferCoverage(offer, def)?.sumInsured;
        return typeof sum === "number" ? { main: formatSumInsured(sum, t) } : null;
      },
    });
    rows.push({
      kind: "metric",
      labelKey: "coverages.costShare.label",
      infoKey: "coverages.costShare.info",
      infoTitleKey: "coverages.costShare.infoTitle",
      value: (offer, t) => {
        const cov = getOfferCoverage(offer, def);
        const current = isYoung ? cov?.deductiblePercentageUpTo9Yrs : cov?.deductiblePercentageOver9Yrs;
        if (typeof current !== "number") return null;
        const otherBand = isYoung ? cov?.deductiblePercentageOver9Yrs : cov?.deductiblePercentageUpTo9Yrs;
        return {
          main: `${100 - current}%`,
          sub:
            typeof otherBand === "number"
              ? t(isYoung ? "priceCustomization.table.otherBandOver9" : "priceCustomization.table.otherBandUpTo9", { pct: 100 - otherBand })
              : undefined,
        };
      },
    });
    rows.push({ kind: "section", labelKey: "coverages.bausteine.label", infoKey: "coverages.bausteine.info", infoTitleKey: "coverages.bausteine.infoTitle", sub: true });
    // The sub-coverages are plain flags (no sum of their own), so each one is listed with a check or a cross.
    for (const sub of def.subs) {
      rows.push({
        kind: "check",
        nested: true,
        labelKey: sub.labelKey,
        infoKey: sub.infoKey,
        infoTitleKey: sub.infoTitleKey,
        value: (offer, t) => {
          // Show what the backend actually included for this package — "fixed" only means the customer cannot
          // deselect it, not that every package contains it.
          const isCovered = !!getOfferCoverage(offer, def)?.[sub.id]?.requested;
          return { isCovered };
        },
      });
    }
  }

  // Contract-level coverages and the daily termination right: shown per package like everything else,
  // so the table is complete and nothing has to be explained in a note.
  rows.push({ kind: "section", labelKey: "priceCustomization.table.furtherBenefits" });
  rows.push({
    kind: "check",
    labelKey: "coverages.rescue.label",
    infoKey: "coverages.rescue.infoDescription",
    infoTitleKey: "coverages.rescue.infoTitle",
    value: (offer, t) => {
      const rescue = getOfferCoverage(offer, getBaseCoverage("rescue"));
      const covered = !!rescue?.requested;
      return {
        isCovered: covered,
        detail:
          covered && typeof rescue?.sumInsured === "number"
            ? t("priceCustomization.table.rescueDetail", { sum: formatChf(rescue.sumInsured) })
            : undefined,
      };
    },
  });
  rows.push({
    kind: "check",
    labelKey: "coverages.emergencyCare.label",
    infoKey: "coverages.emergencyCare.infoDescription",
    infoTitleKey: "coverages.emergencyCare.infoTitle",
    value: (offer, t) => {
      const care = (offer?.product?.contractCoverages as Record<string, any> | undefined)?.emergencyCare;
      const covered = !!care?.requested;
      return {
        isCovered: covered,
        hideCheckmark: true,
        detail:
          covered && care.sumInsuredPerDay != null
            ? t("priceCustomization.table.emergencyCareDetail", { amount: formatChf(Number(care.sumInsuredPerDay)), days: care.numberDays ?? 20 })
            : undefined,
      };
    },
  });
  rows.push({
    kind: "check",
    labelKey: "coverages.dailyTerminationLabel",
    infoKey: "coverages.dailyTerminationInfo",
    value: (offer, t) => {
      const selected = !!offer?.dailyTermination?.selected;
      return { isCovered: selected };
    },
  });

  return rows;
};
